/**
 * ===========================================================
 * FeatureManager Unified Helper
 * -----------------------------------------------------------
 * Centraliza:
 *  - Feature flags booleanos
 *  - Experimentos multi-variante (A/B/C)
 *  - Overrides locales (Customizer / API)
 *  - Reactividad vía GrowthBook renderer
 * ===========================================================
 */

window.FeatureManager = (() => {
  const HOLDOUT_KEY = "circulon-holdout";

  const featureCallbacks = new Map(); // boolean listeners
  const variantCallbacks = new Map(); // variant listeners

  let localConfig = {};
  let hasRenderedOnce = false;

  /* ---------------------------------------------------------
   * GrowthBook getter
   * --------------------------------------------------------- */

  function getGrowthBook() {
    return window._growthbook;
  }

  /* ---------------------------------------------------------
   * CONFIG MANAGEMENT
   * --------------------------------------------------------- */

  function initConfig(config = {}) {
    localConfig = { ...localConfig, ...config };
  }

  function getConfig() {
    return { ...localConfig };
  }

  function getLocalValue(featureKey) {
    return localConfig.hasOwnProperty(featureKey)
      ? localConfig[featureKey]
      : undefined;
  }

  /* ---------------------------------------------------------
   * FEATURE BOOLEAN EVALUATION
   * --------------------------------------------------------- */

  function isEnabled(featureKey) {
    const gb = getGrowthBook();

    if (!gb) {
      console.warn("GrowthBook not initialized.");
      return false;
    }

    if (gb.isOn(HOLDOUT_KEY)) return false;

    return gb.isOn(featureKey);
  }

  function getEffectiveFeatureState({ featureKey, defaultValue = false }) {
    const localValue = getLocalValue(featureKey);

    if (localValue === true) return true;
    if (localValue === false) return false;

    if (isEnabled(featureKey)) return true;

    return defaultValue;
  }

  /* ---------------------------------------------------------
   * MULTI-VARIANT SUPPORT
   * --------------------------------------------------------- */

  /**
   * Devuelve la variante activa de un experimento (A/B/C/etc)
   */
  function getVariant(featureKey, defaultValue = null) {
    const localValue = getLocalValue(featureKey);

    if (localValue !== undefined) {
      return localValue;
    }

    const gb = getGrowthBook();
    if (!gb) return defaultValue;

    if (gb.isOn(HOLDOUT_KEY)) return defaultValue;

    return gb.getFeatureValue(featureKey, defaultValue);
  }

  /* ---------------------------------------------------------
   * REACTIVITY
   * --------------------------------------------------------- */

  function onFeatureChange(featureKey, callback, runImmediately = true) {
    if (!featureCallbacks.has(featureKey)) {
      featureCallbacks.set(featureKey, []);
    }

    featureCallbacks.get(featureKey).push(callback);

    if (runImmediately) {
      callback(getEffectiveFeatureState({ featureKey }));
    }
  }

  function onVariantChange(featureKey, callback, runImmediately = true) {
    if (!variantCallbacks.has(featureKey)) {
      variantCallbacks.set(featureKey, []);
    }

    variantCallbacks.get(featureKey).push(callback);

    if (runImmediately) {
      callback(getVariant(featureKey));
    }
  }

  /* ---------------------------------------------------------
   * DOM HELPERS
   * --------------------------------------------------------- */

  function toggleClass(featureKey, selector, className) {
    onFeatureChange(featureKey, (enabled) => {
      document.querySelectorAll(selector).forEach((el) => {
        el.classList.toggle(className, enabled);
      });
    });
  }

  /**
   * Aplica clases basadas en variante
   * Ej: prefix-a, prefix-b, prefix-control
   */
  function applyVariantClass(featureKey, selector, classPrefix = "") {
    onVariantChange(featureKey, (variant) => {
      document.querySelectorAll(selector).forEach((el) => {
        el.classList.forEach((cls) => {
          if (cls.startsWith(classPrefix)) {
            el.classList.remove(cls);
          }
        });

        if (variant) {
          el.classList.add(`${classPrefix}${variant}`);
        }
      });
    });
  }

  /* ---------------------------------------------------------
   * GrowthBook Renderer
   * --------------------------------------------------------- */

  function initRenderer() {
    const gb = getGrowthBook();
    if (!gb?.setRenderer) return;

    gb.setRenderer(() => {
      // Boolean features
      featureCallbacks.forEach((callbacks, featureKey) => {
        const enabled = getEffectiveFeatureState({ featureKey });
        callbacks.forEach((cb) => cb(enabled));
      });

      // Variants
      variantCallbacks.forEach((callbacks, featureKey) => {
        const value = getVariant(featureKey);
        callbacks.forEach((cb) => cb(value));
      });

      if (!hasRenderedOnce && gb.ready) {
        hasRenderedOnce = true;
        window.dispatchEvent(
          new CustomEvent("featureManager::ready")
        );
      }
    });

    gb.refreshFeatures();
  }

  /* ---------------------------------------------------------
   * Wait for GrowthBook
   * --------------------------------------------------------- */

  function waitForGrowthBook() {
    const interval = setInterval(() => {
      if (getGrowthBook()) {
        clearInterval(interval);
        initRenderer();
      }
    }, 50);
  }

  waitForGrowthBook();

  /* ---------------------------------------------------------
   * PUBLIC API
   * --------------------------------------------------------- */

  return {
    // Config
    initConfig,
    getConfig,
    getLocalValue,

    // Boolean features
    isEnabled,
    getEffectiveFeatureState,
    onFeatureChange,
    toggleClass,

    // Variants
    getVariant,
    onVariantChange,
    applyVariantClass,
  };
})();

/**
 * Helpers para identificar tipo de página en Shopify
 * Se basan en el body tag y la variable window.Shopify
 */

function isHomePage() {
  return document.body.classList.contains('template-index');
}

function isProductPage() {
  return document.body.classList.contains('template-product');
}

function isCollectionPage() {
  return document.body.classList.contains('template-collection');
}

function isSearchPage() {
  return document.body.classList.contains('template-search');
}

function isPage() {
    return document.body.classList.contains('template-page');
}

// test architecture

window.addEventListener('featureManager::ready', () => {

  function updateArchitectureInfo(enabled) {
    const originalElements = document.querySelectorAll('.pdm-original-class');
    const testElements = document.querySelectorAll('.pdm-test-class');

    originalElements.forEach(el => {
      el.classList.toggle('pdm-is-hidden', enabled);
    });

    testElements.forEach(el => {
      el.classList.toggle('pdm-is-hidden', !enabled);
    });
  }

  const enabled = FeatureManager.getEffectiveFeatureState({
    featureKey: 'circulon-halp-lp-info-architecture',
  });

  updateArchitectureInfo(enabled);

  FeatureManager.onFeatureChange(
    'circulon-halp-lp-info-architecture',
    updateArchitectureInfo
  );
});


// circulon-sd-product-carousel test

window.addEventListener('featureManager::ready', () => {

  function updateProductCarouselDisplay(enabled) {
    console.log('circulon-sd-product-carousel-display-lp variant:', enabled );

    const variant = FeatureManager.getVariant('circulon-sd-product-carousel-display-lp', 'control');

    const originalElements = document.querySelectorAll('.pdm-test-original');
    const variantBElements = document.querySelectorAll('.pdm-test-b');
    const variantCElements = document.querySelectorAll('.pdm-test-c');

    console.log('esta es la variante 2:', typeof variant, variant );

    if (variant === 'B') {
      console.clear();
      console.log('variantBElements:', variantBElements );
      variantBElements.forEach(el => el.classList.remove('hidden-important'));
      originalElements.forEach(el => el.classList.add('hidden-important'));
      variantCElements.forEach(el => el.classList.add('hidden-important'));
    } else if (variant === 'C') {
      console.clear();
      console.log('variantCElements:', variantCElements);
      variantCElements.forEach(el => el.classList.remove('hidden-important'));
      originalElements.forEach(el => el.classList.add('hidden-important'));
      variantBElements.forEach(el => el.classList.add('hidden-important'));
    } else {
      console.clear();
      console.log('originalElements:', originalElements);
      originalElements.forEach(el => el.classList.remove('hidden-important'));
      variantBElements.forEach(el => el.classList.add('hidden-important'));
      variantCElements.forEach(el => el.classList.add('hidden-important'));
    }
  } 

  FeatureManager.onFeatureChange('circulon-sd-product-carousel-display-lp', updateProductCarouselDisplay);

});